import React, { useEffect, useMemo, useState } from 'react';
import { useActivityMutations, useLeadMutations } from '../hooks/useCRM';
import { useToast } from './Toast';
import '../crm.css';

const TABS = [
    { id: 'call', label: 'Call' },
    { id: 'whatsapp', label: 'WhatsApp' },
    { id: 'email', label: 'Email' },
    { id: 'note', label: 'Note' },
];

const defaultState = {
    title: '',
    description: '',
    nextFollowUpDate: '',
    nextFollowUpTime: '',
};

function QuickLogDrawer() {
    const { create: createActivity } = useActivityMutations();
    const { logCommunication, update } = useLeadMutations();
    const { showSuccess, showError } = useToast();
    const [isOpen, setIsOpen] = useState(false);
    const [activeTab, setActiveTab] = useState('call');
    const [entity, setEntity] = useState(null);
    const [form, setForm] = useState(defaultState);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        const handleOpen = (event) => {
            setEntity(event.detail || null);
            setForm(defaultState);
            setActiveTab('call');
            setIsOpen(true);
        };
        const handleEscape = (event) => {
            if (event.key === 'Escape') setIsOpen(false);
        };
        window.addEventListener('crm:quickLog', handleOpen);
        window.addEventListener('keydown', handleEscape);
        return () => {
            window.removeEventListener('crm:quickLog', handleOpen);
            window.removeEventListener('keydown', handleEscape);
        };
    }, []);

    const handleChange = (field, value) => {
        setForm((prev) => ({ ...prev, [field]: value }));
    };

    const computeNextFollowUp = () => {
        if (!form.nextFollowUpDate) return null;
        const time = form.nextFollowUpTime || '09:00';
        const date = new Date(`${form.nextFollowUpDate}T${time}`);
        if (Number.isNaN(date.getTime())) return null;
        return date.toISOString();
    };

    const handleSubmit = async () => {
        if (!form.title.trim() || !entity) return;
        setSaving(true);
        try {
            if (entity.entityType === 'lead') {
                if (activeTab === 'note') {
                    await createActivity({
                        type: 'note',
                        title: form.title,
                        description: form.description || '',
                        entityType: 'lead',
                        entityId: entity.entityId,
                        entityName: entity.entityName,
                    });
                } else {
                    await logCommunication(entity.entityId, {
                        channel: activeTab,
                        title: form.title,
                        description: form.description || undefined,
                    });
                }
                const nextFollowUpAt = computeNextFollowUp();
                if (nextFollowUpAt) {
                    await update(entity.entityId, { nextFollowUpAt });
                }
            } else {
                await createActivity({
                    type: activeTab === 'note' ? 'note' : activeTab,
                    title: form.title,
                    description: form.description || '',
                    entityType: entity.entityType,
                    entityId: entity.entityId,
                    entityName: entity.entityName,
                });
            }
            showSuccess('Logged successfully');
            setIsOpen(false);
        } catch (err) {
            console.error('Quick log failed:', err);
            showError('Failed to log activity');
        } finally {
            setSaving(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="crm-drawer-overlay" onClick={() => setIsOpen(false)}>
            <div className="crm-drawer" onClick={(event) => event.stopPropagation()}>
                <div className="crm-drawer-header">
                    <div>
                        <h3>Quick Log</h3>
                        <p>{entity?.entityName || ''}</p>
                    </div>
                    <button className="crm-modal-close" onClick={() => setIsOpen(false)}>X</button>
                </div>
                <div className="crm-tab-bar">
                    {TABS.map((tab) => (
                        <button
                            key={tab.id}
                            className={`crm-tab ${activeTab === tab.id ? 'active' : ''}`}
                            onClick={() => setActiveTab(tab.id)}
                            type="button"
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>
                <div className="crm-drawer-body">
                    <div className="crm-form-group">
                        <label className="crm-label required">Title</label>
                        <input
                            className="crm-input"
                            value={form.title}
                            onChange={(event) => handleChange('title', event.target.value)}
                            placeholder="Summary"
                        />
                    </div>
                    <div className="crm-form-group">
                        <label className="crm-label">Description</label>
                        <textarea
                            className="crm-textarea"
                            rows={4}
                            value={form.description}
                            onChange={(event) => handleChange('description', event.target.value)}
                            placeholder="Details"
                        />
                    </div>
                    {entity?.entityType === 'lead' && (
                        <div className="crm-form-row">
                            <div className="crm-form-group">
                                <label className="crm-label">Next follow-up date</label>
                                <input
                                    type="date"
                                    className="crm-input"
                                    value={form.nextFollowUpDate}
                                    onChange={(event) => handleChange('nextFollowUpDate', event.target.value)}
                                />
                            </div>
                            <div className="crm-form-group">
                                <label className="crm-label">Time</label>
                                <input
                                    type="time"
                                    className="crm-input"
                                    value={form.nextFollowUpTime}
                                    onChange={(event) => handleChange('nextFollowUpTime', event.target.value)}
                                />
                            </div>
                        </div>
                    )}
                    <div className="crm-form-actions">
                        <button className="crm-btn crm-btn-secondary" onClick={() => setIsOpen(false)}>
                            Cancel
                        </button>
                        <button
                            className="crm-btn crm-btn-primary"
                            onClick={handleSubmit}
                            disabled={saving || !form.title.trim()}
                        >
                            {saving ? 'Saving...' : 'Save'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default QuickLogDrawer;
